import { useMemo } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Chip from '@mui/material/Chip'
import ExtensionIcon from '@mui/icons-material/Extension'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import { FormattedMessage } from 'react-intl'
import { get } from 'lodash'

const FEATURE_ICONS = {
  docker: '/icons/docker.svg',
  tailscale: '/icons/tailscale.svg',
  fail2ban: '/icons/fail2ban.svg',
}

function StatusChip({ status }) {
  const statusConfig = useMemo(() => {
    switch (status) {
      case 'installed':
        return { color: 'success', icon: <CheckCircleIcon fontSize="small" />, labelId: 'nodes.features.status.installed' }
      case 'pending':
        return { color: 'warning', icon: <HourglassEmptyIcon fontSize="small" />, labelId: 'nodes.features.status.pending' }
      case 'failed':
        return { color: 'error', icon: <ErrorIcon fontSize="small" />, labelId: 'nodes.features.status.failed' }
      default:
        return { color: 'default', icon: null, labelId: 'nodes.features.status.notInstalled' }
    }
  }, [status])

  return (
    <Chip
      size="small"
      color={statusConfig.color}
      icon={statusConfig.icon}
      label={<FormattedMessage id={statusConfig.labelId} />}
    />
  )
}

function NodeFeatureCard({ featureType, feature, onInstall, onUninstall, loading }) {
  const isInstalled = Boolean(feature)
  const status = get(feature, 'status', null)
  const version = get(feature, 'version', null)
  const error = get(feature, 'error', null)

  const handleAction = () => {
    if (isInstalled) {
      onUninstall(feature)
    } else {
      onInstall(featureType)
    }
  }

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        p: 2,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'action.hover',
            borderRadius: 1,
            mr: 2,
          }}
        >
          <ExtensionIcon sx={{ color: 'text.secondary' }} />
        </Box>
        <Box>
          <Typography variant="h6" component="h3">
            <FormattedMessage id={`nodes.features.${featureType}.name`} />
          </Typography>
        </Box>
      </Box>

      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 2, flexGrow: 1 }}
      >
        <FormattedMessage id={`nodes.features.${featureType}.description`} />
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <StatusChip status={status} />
        {version && (
          <Typography variant="caption" color="text.secondary">
            v{version}
          </Typography>
        )}
      </Box>

      {error && status === 'failed' && (
        <Typography variant="body2" color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      <Box>
        <Button
          variant="outlined"
          size="small"
          color={isInstalled ? 'error' : 'primary'}
          onClick={handleAction}
          disabled={loading || status === 'pending'}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {isInstalled ? (
            <FormattedMessage id="nodes.features.actions.uninstall" />
          ) : (
            <FormattedMessage id="nodes.features.actions.install" />
          )}
        </Button>
      </Box>
    </Box>
  )
}

export default NodeFeatureCard
